import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Search,
  Person,
  Email,
  Phone,
  Work,
  School,
  LocationOn,
  Edit,
  Delete,
  Visibility,
  Block,
  CheckCircle,
  AdminPanelSettings,
  PersonAdd,
  FilterList,
  Download,
  Refresh
} from '@mui/icons-material';
import axios from 'axios';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    candidates: 0,
    recruiters: 0,
    admins: 0,
    active: 0,
    inactive: 0
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, statusFilter, tabValue]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [usersResponse, applicationsResponse] = await Promise.all([
        axios.get('http://localhost:5000/api/admin/users', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('http://localhost:5000/api/applications/admin/all', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const userData = usersResponse.data.data || [];
      const applicationsData = applicationsResponse.data.data || [];
      
      // Add application count to each user
      const usersWithStats = userData.map(user => {
        const userApplications = applicationsData.filter(app => 
          app.candidate && app.candidate._id === user._id
        );
        return {
          ...user,
          applicationCount: userApplications.length,
          lastApplicationDate: userApplications.length > 0 ? 
            new Date(Math.max(...userApplications.map(app => new Date(app.createdAt)))) : null
        };
      });
      
      setUsers(usersWithStats);
      
      // Calculate stats
      const stats = {
        total: usersWithStats.length,
        candidates: usersWithStats.filter(u => u.role === 'candidate').length,
        recruiters: usersWithStats.filter(u => u.role === 'recruiter').length,
        admins: usersWithStats.filter(u => u.role === 'admin').length,
        active: usersWithStats.filter(u => u.isActive).length,
        inactive: usersWithStats.filter(u => !u.isActive).length
      };
      setStats(stats);
      
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];
    
    // Tab filter
    if (tabValue === 1) filtered = filtered.filter(u => u.role === 'candidate');
    else if (tabValue === 2) filtered = filtered.filter(u => u.role === 'recruiter');
    else if (tabValue === 3) filtered = filtered.filter(u => u.role === 'admin');
    
    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }
    
    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(u => u.isActive);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(u => !u.isActive);
    }
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(u => 
        u.email?.toLowerCase().includes(search) ||
        u.profile?.firstName?.toLowerCase().includes(search) ||
        u.profile?.lastName?.toLowerCase().includes(search) ||
        u.profile?.phone?.includes(search)
      );
    }
    
    setFilteredUsers(filtered);
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `http://localhost:5000/api/admin/users/${userId}/toggle-status`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch (err) {
      console.error('Error toggling user status:', err);
      setError('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `http://localhost:5000/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('User deleted successfully');
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleColor = (role) => {
    const colors = {
      'admin': 'error',
      'recruiter': 'warning',
      'candidate': 'primary',
      'interviewer': 'secondary'
    };
    return colors[role] || 'default';
  };

  const exportUsers = () => {
    const csvContent = [
      ['Email', 'Name', 'Role', 'Status', 'Phone', 'Joined Date', 'Last Visit'].join(','),
      ...filteredUsers.map(user => [
        user.email,
        `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`,
        user.role,
        user.isActive ? 'Active' : 'Inactive',
        user.profile?.phone || '',
        formatDate(user.createdAt),
        formatDate(user.lastLogin)
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={exportUsers}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchUsers}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => navigate('/register')}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4">{stats.total}</Typography>
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="primary">{stats.candidates}</Typography>
              <Typography variant="body2" color="text.secondary">
                Candidates
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="warning.main">{stats.recruiters}</Typography>
              <Typography variant="body2" color="text.secondary">
                Recruiters
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="error">{stats.admins}</Typography>
              <Typography variant="body2" color="text.secondary">
                Admins
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="success.main">{stats.active}</Typography>
              <Typography variant="body2" color="text.secondary">
                Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="text.secondary">{stats.inactive}</Typography>
              <Typography variant="body2" color="text.secondary">
                Inactive
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Role Filter</InputLabel>
              <Select
                value={roleFilter}
                label="Role Filter"
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="candidate">Candidates</MenuItem>
                <MenuItem value="recruiter">Recruiters</MenuItem>
                <MenuItem value="admin">Admins</MenuItem>
                <MenuItem value="interviewer">Interviewers</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label={<Badge badgeContent={stats.total} color="default">All Users</Badge>} />
          <Tab label={<Badge badgeContent={stats.candidates} color="primary">Candidates</Badge>} />
          <Tab label={<Badge badgeContent={stats.recruiters} color="warning">Recruiters</Badge>} />
          <Tab label={<Badge badgeContent={stats.admins} color="error">Admins</Badge>} />
        </Tabs>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Visit</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((user) => (
                <TableRow key={user._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2 }}>
                        {user.profile?.firstName?.[0] || user.email[0].toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">
                          {user.profile?.firstName} {user.profile?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {user._id.slice(-6)}
                        </Typography>
                        {user.role === 'candidate' && user.applicationCount > 0 && (
                          <Chip
                            label={`${user.applicationCount} apps`}
                            size="small"
                            color="primary"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{user.profile?.phone || '-'}</TableCell>
                  <TableCell>
                    {user.profile?.address?.city && user.profile?.address?.state ? 
                      `${user.profile.address.city}, ${user.profile.address.state}` : 
                      '-'}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {formatDate(user.createdAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(user.createdAt).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" color={user.lastLogin ? 'text.primary' : 'text.secondary'}>
                        {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                      </Typography>
                      {user.lastLogin && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(user.lastLogin).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Active' : 'Inactive'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedUser(user);
                          setViewDialog(true);
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={user.isActive ? 'Deactivate' : 'Activate'}>
                      <IconButton
                        size="small"
                        color={user.isActive ? 'warning' : 'success'}
                        onClick={() => handleToggleUserStatus(user._id, user.isActive)}
                      >
                        {user.isActive ? <Block /> : <CheckCircle />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete User">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteUser(user._id)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        {filteredUsers.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No users found
            </Typography>
          </Box>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* View User Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent dividers>
          {selectedUser && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body1">{selectedUser.email}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Role</Typography>
                <Typography variant="body1">
                  <Chip
                    label={selectedUser.role}
                    color={getRoleColor(selectedUser.role)}
                    size="small"
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">First Name</Typography>
                <Typography variant="body1">{selectedUser.profile?.firstName || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Last Name</Typography>
                <Typography variant="body1">{selectedUser.profile?.lastName || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{selectedUser.profile?.phone || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography variant="body1">
                  <Chip
                    label={selectedUser.isActive ? 'Active' : 'Inactive'}
                    color={selectedUser.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Address</Typography>
                <Typography variant="body1">
                  {selectedUser.profile?.address?.street && selectedUser.profile?.address?.city ? 
                    `${selectedUser.profile.address.street}, ${selectedUser.profile.address.city}, ${selectedUser.profile.address.state || ''} ${selectedUser.profile.address.zipCode || ''}` : 
                    '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Profile Created</Typography>
                <Typography variant="body1">{formatDateTime(selectedUser.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Last Visit</Typography>
                <Typography variant="body1">
                  {selectedUser.lastLogin ? formatDateTime(selectedUser.lastLogin) : 'Never logged in'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Email Verified</Typography>
                <Typography variant="body1">
                  <Chip
                    label={selectedUser.isEmailVerified ? 'Verified' : 'Not Verified'}
                    color={selectedUser.isEmailVerified ? 'success' : 'warning'}
                    size="small"
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Account Status</Typography>
                <Typography variant="body1">
                  <Chip
                    label={selectedUser.isActive ? 'Active' : 'Inactive'}
                    color={selectedUser.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Typography>
              </Grid>
              {selectedUser.role === 'candidate' && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Applications</Typography>
                    <Typography variant="body1">
                      {selectedUser.applicationCount || 0} applications
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Last Application</Typography>
                    <Typography variant="body1">
                      {selectedUser.lastApplicationDate ? formatDate(selectedUser.lastApplicationDate) : 'No applications'}
                    </Typography>
                  </Grid>
                </>
              )}
              {selectedUser.role === 'candidate' && selectedUser.profile && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ mt: 2 }}>Candidate Profile</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Education</Typography>
                    <Typography variant="body1">
                      {selectedUser.profile.education || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Experience</Typography>
                    <Typography variant="body1">
                      {selectedUser.profile.experience || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                    <Typography variant="body1">
                      {selectedUser.profile.dateOfBirth ? formatDate(selectedUser.profile.dateOfBirth) : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Bio</Typography>
                    <Typography variant="body1">
                      {selectedUser.profile.bio || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Skills</Typography>
                    <Box sx={{ mt: 1 }}>
                      {selectedUser.profile.skills?.map((skill, index) => (
                        <Chip
                          key={index}
                          label={skill}
                          size="small"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      )) || '-'}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Languages</Typography>
                    <Box sx={{ mt: 1 }}>
                      {selectedUser.profile.languages?.map((lang, index) => (
                        <Chip
                          key={index}
                          label={`${lang.name} (${Object.entries(lang).filter(([key, value]) => key !== 'name' && value).map(([key]) => key.replace('can', '')).join(', ')})`}
                          size="small"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      )) || '-'}
                    </Box>
                  </Grid>
                  {selectedUser.profile.socialLinks && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Social Links</Typography>
                      <Box sx={{ mt: 1 }}>
                        {selectedUser.profile.socialLinks.linkedin && (
                          <Chip
                            label="LinkedIn"
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                            color="primary"
                          />
                        )}
                        {selectedUser.profile.socialLinks.github && (
                          <Chip
                            label="GitHub"
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                            color="default"
                          />
                        )}
                        {selectedUser.profile.socialLinks.portfolio && (
                          <Chip
                            label="Portfolio"
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                            color="secondary"
                          />
                        )}
                      </Box>
                    </Grid>
                  )}
                  {selectedUser.profile.documents && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Documents</Typography>
                      <Box sx={{ mt: 1 }}>
                        {selectedUser.profile.documents.resume && (
                          <Chip
                            label="Resume"
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                            color="success"
                          />
                        )}
                        {selectedUser.profile.documents.coverLetter && (
                          <Chip
                            label="Cover Letter"
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                            color="info"
                          />
                        )}
                        {selectedUser.profile.documents.certificates?.length > 0 && (
                          <Chip
                            label={`${selectedUser.profile.documents.certificates.length} Certificates`}
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                            color="warning"
                          />
                        )}
                      </Box>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUsers;
